import type { RenderState } from "../game/types";
import { BrandMark } from "./BrandMark";

interface ResultsScreenProps {
  state: RenderState;
  practice: boolean;
  localName: string;
  opponentName: string;
  onPlayAgain: () => void;
  onHome: () => void;
  reportAvailable?: boolean;
  reportMessage?: string | null;
  onReport?: () => void;
}

export function ResultsScreen({
  state,
  practice,
  localName,
  opponentName,
  onPlayAgain,
  onHome,
  reportAvailable = false,
  reportMessage = null,
  onReport,
}: ResultsScreenProps) {
  const authoritativeWinner = practice ? null : state.winner;
  const winner = authoritativeWinner ?? (state.scoreLocal === state.scoreOpponent ? "draw" : state.scoreLocal > state.scoreOpponent ? "local" : "opponent");
  const result = winner === "draw" ? "DRAW" : winner === "local" ? "VICTORY" : "DEFEAT";
  const durationSeconds = Math.round(state.stats.elapsedMs / 1_000);

  return (
    <main className={`results-screen results-screen--${result.toLowerCase()}`}>
      <header><BrandMark compact /><span>{practice ? "Local practice" : "Server result"}</span></header>
      <section className="results-card">
        <div className="results-burst" aria-hidden="true"><i /><i /><i /></div>
        <p className="eyebrow"><span /> Match complete</p>
        <h1>{result}</h1>
        <p>{practice ? "Practice simulation complete" : `Final ${state.finishReason ?? "match"} result received from the authoritative match server`}</p>
        <div className="final-score" aria-label={`Final score ${localName} ${state.scoreLocal}, ${opponentName} ${state.scoreOpponent}`}>
          <div><span className="player-avatar">{initial(localName)}</span><small>You</small><strong>{localName}</strong></div>
          <b>{state.scoreLocal}<em>:</em>{state.scoreOpponent}</b>
          <div><span className="player-avatar player-avatar--opponent">{initial(opponentName)}</span><small>{practice ? "Bot" : "Opponent"}</small><strong>{opponentName}</strong></div>
        </div>
        <dl className="match-stats">
          <div><dt>Duration</dt><dd>{formatDuration(durationSeconds)}</dd></div>
          <div><dt>Touches</dt><dd>{practice ? state.stats.touches : "—"}</dd></div>
          <div><dt>Max puck speed</dt><dd>{state.stats.maxPuckSpeed > 0 ? `${Math.round(state.stats.maxPuckSpeed)} u/s` : "—"}</dd></div>
        </dl>
        {!practice && <p className="honesty-note">The result is server-authoritative. Profile and season updates are shown only when returned by the account service.</p>}
        {!practice && reportMessage && <p className="report-availability" role="status">{reportMessage}</p>}
        <div className="results-actions">
          {practice && <button className="primary-button" type="button" onClick={onPlayAgain}>Practice again <span>↻</span></button>}
          {!practice && onReport && (
            <button className="danger-button" type="button" onClick={onReport} disabled={!reportAvailable}>Report opponent</button>
          )}
          <button className={practice ? "secondary-button" : "primary-button"} type="button" onClick={onHome}>Return home</button>
        </div>
      </section>
    </main>
  );
}

function initial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "P";
}

function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  return `${minutes}:${String(totalSeconds % 60).padStart(2, "0")}`;
}
